import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from './ui/button'
import { Cast, Check } from 'lucide-react'
import { sendCast } from '../cast'
import { parseVideoInput } from '../video'

type Status = 'idle' | 'sending' | 'sent' | 'error'

export function CastReceiver() {
  const { session } = useParams<{ session: string }>()
  const [url, setUrl] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [lastSent, setLastSent] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!session) return
    setError(null)
    const parsed = parseVideoInput(url)
    if (!parsed) {
      setError('URL no reconocida. Usa una URL completa de YouTube o un ID directo.')
      return
    }
    setStatus('sending')
    try {
      const res = await sendCast(session, url)
      setLastSent(res.video_id)
      setUrl('')
      setStatus('sent')
      setTimeout(() => setStatus('idle'), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al enviar')
      setStatus('error')
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-zinc-950 text-zinc-100 p-6">
      <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
        <header className="flex items-center gap-3">
          <Cast className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Enviar video a la TV</h1>
        </header>

        <p className="text-sm text-zinc-400">
          Pega la URL de un video de YouTube. La TV lo agrega a la rotación de fondo
          en pocos segundos.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            inputMode="url"
            autoFocus
            placeholder="https://www.youtube.com/watch?v=..."
            aria-label="URL del video"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-3 text-base font-mono text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none"
          />
          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}
          <Button type="submit" disabled={status === 'sending' || !url.trim()} className="w-full" size="lg">
            {status === 'sending' ? 'Enviando…' : status === 'sent' ? (
              <>
                <Check className="h-5 w-5" /> Enviado
              </>
            ) : (
              'Enviar a la TV'
            )}
          </Button>
        </form>

        {lastSent && (
          <p data-testid="last-sent" className="text-xs text-zinc-500">
            Último enviado: <code className="font-mono">{lastSent}</code>
          </p>
        )}
      </div>
    </div>
  )
}
